import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronDown, ChevronUp, Plus, Pencil, Trash2 } from 'lucide-react'
import { recruitmentAdminApi } from '../../../services/api'
import Button from '../../../components/ui/Button'
import LoadingSpinner from '../../../components/ui/LoadingSpinner'
import Card from '../../../components/ui/Card'
import AdminFormField, { adminInputClass } from '../../../components/admin/AdminFormField'

const EMPTY_DEPARTMENT = { name_en: '', name_ar: '', slug: '', is_active: true, sort_order: 0 }
const EMPTY_SPECIALIZATION = { name_en: '', name_ar: '', slug: '', is_active: true, sort_order: 0 }

function SpecializationsPanel({ department, onChanged }) {
  const { t } = useTranslation()
  const [items, setItems] = useState(department.specializations || [])
  const [form, setForm] = useState(null)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    setItems(department.specializations || [])
  }, [department])

  const load = async () => {
    const { data } = await recruitmentAdminApi.departments.specializations.getAll(department.id)
    setItems(data.data || data || [])
    onChanged()
  }

  const handleSave = async (e) => {
    e.preventDefault()
    setSaving(true)
    try {
      if (form.id) {
        await recruitmentAdminApi.departments.specializations.update(department.id, form.id, form)
      } else {
        await recruitmentAdminApi.departments.specializations.create(department.id, form)
      }
      setForm(null)
      await load()
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="mt-4 border-t border-border pt-4">
      <div className="mb-3 flex items-center justify-between">
        <h4 className="font-medium text-primary">{t('admin.recruitment.specializations')}</h4>
        <Button type="button" variant="outlineDark" size="sm" onClick={() => setForm(EMPTY_SPECIALIZATION)}>
          <Plus className="h-4 w-4" />
          {t('admin.recruitment.addSpecialization')}
        </Button>
      </div>

      {form ? (
        <form onSubmit={handleSave} className="mb-4 grid gap-3 rounded-xl bg-primary/5 p-4 sm:grid-cols-2">
          <AdminFormField label={t('admin.recruitment.fields.nameEn')} htmlFor={`spec-en-${department.id}`}>
            <input
              id={`spec-en-${department.id}`}
              value={form.name_en}
              onChange={(e) => setForm({ ...form, name_en: e.target.value })}
              className={adminInputClass}
              required
            />
          </AdminFormField>
          <AdminFormField label={t('admin.recruitment.fields.nameAr')} htmlFor={`spec-ar-${department.id}`}>
            <input
              id={`spec-ar-${department.id}`}
              value={form.name_ar}
              onChange={(e) => setForm({ ...form, name_ar: e.target.value })}
              dir="rtl"
              lang="ar"
              className={`${adminInputClass} font-ar`}
              required
            />
          </AdminFormField>
          <AdminFormField label={t('admin.recruitment.fields.sortOrder')} htmlFor={`spec-sort-${department.id}`}>
            <input
              id={`spec-sort-${department.id}`}
              type="number"
              value={form.sort_order}
              onChange={(e) => setForm({ ...form, sort_order: +e.target.value })}
              className={adminInputClass}
            />
          </AdminFormField>
          <div className="flex items-end gap-2">
            <Button type="submit" variant="primary" size="sm" disabled={saving}>{t('buttons.save')}</Button>
            <Button type="button" variant="ghost" size="sm" onClick={() => setForm(null)}>{t('buttons.cancel')}</Button>
          </div>
        </form>
      ) : null}

      {items.length ? (
        <div className="space-y-2">
          {items.map((item) => (
            <div key={item.id} className="flex items-center justify-between rounded-xl border border-border px-3 py-2 text-sm">
              <div>
                <span className="font-medium">{item.name_en}</span>
                <span className="mx-2 text-text/30">·</span>
                <span className="text-text/60">{item.name_ar}</span>
              </div>
              <div className="flex gap-1">
                <button type="button" onClick={() => setForm(item)} className="p-2 text-primary">
                  <Pencil className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={async () => {
                    await recruitmentAdminApi.departments.specializations.delete(department.id, item.id)
                    await load()
                  }}
                  className="p-2 text-red-600"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-sm text-text/60">{t('admin.recruitment.noSpecializations')}</p>
      )}
    </div>
  )
}

export default function AdminJobDepartments() {
  const { t } = useTranslation()
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(true)
  const [form, setForm] = useState(null)
  const [saving, setSaving] = useState(false)
  const [expandedId, setExpandedId] = useState(null)

  const load = () => {
    setLoading(true)
    recruitmentAdminApi.departments
      .getAll()
      .then(({ data }) => setItems(data.data || data || []))
      .finally(() => setLoading(false))
  }

  useEffect(load, [])

  const handleSave = async (e) => {
    e.preventDefault()
    setSaving(true)
    try {
      if (form.id) await recruitmentAdminApi.departments.update(form.id, form)
      else await recruitmentAdminApi.departments.create(form)
      setForm(null)
      load()
    } finally {
      setSaving(false)
    }
  }

  return (
    <div>
      <div className="mb-6 flex justify-end">
        <Button onClick={() => setForm(EMPTY_DEPARTMENT)} variant="accent" size="sm">
          <Plus className="h-4 w-4" />
          {t('admin.recruitment.addDepartment')}
        </Button>
      </div>

      {form && (
        <Card className="mb-6">
          <form onSubmit={handleSave} className="grid gap-4 sm:grid-cols-2">
            <AdminFormField label={t('admin.recruitment.fields.nameEn')} htmlFor="department-name-en">
              <input
                id="department-name-en"
                value={form.name_en}
                onChange={(e) => setForm({ ...form, name_en: e.target.value })}
                className={adminInputClass}
                required
              />
            </AdminFormField>
            <AdminFormField label={t('admin.recruitment.fields.nameAr')} htmlFor="department-name-ar">
              <input
                id="department-name-ar"
                value={form.name_ar}
                onChange={(e) => setForm({ ...form, name_ar: e.target.value })}
                dir="rtl"
                lang="ar"
                className={`${adminInputClass} font-ar`}
                required
              />
            </AdminFormField>
            <AdminFormField label={t('admin.pages.slug')} htmlFor="department-slug">
              <input
                id="department-slug"
                value={form.slug}
                onChange={(e) => setForm({ ...form, slug: e.target.value })}
                className={adminInputClass}
              />
            </AdminFormField>
            <AdminFormField label={t('admin.recruitment.fields.sortOrder')} htmlFor="department-sort-order">
              <input
                id="department-sort-order"
                type="number"
                value={form.sort_order}
                onChange={(e) => setForm({ ...form, sort_order: +e.target.value })}
                className={adminInputClass}
              />
            </AdminFormField>
            <div className="flex gap-2 sm:col-span-2">
              <Button type="submit" variant="primary" disabled={saving}>{t('buttons.save')}</Button>
              <Button type="button" variant="ghost" onClick={() => setForm(null)}>{t('buttons.cancel')}</Button>
            </div>
          </form>
        </Card>
      )}

      {loading ? (
        <LoadingSpinner className="py-20" />
      ) : (
        <div className="space-y-3">
          {items.map((item) => (
            <Card key={item.id}>
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-semibold text-primary">{item.name_en}</div>
                  <div className="text-sm text-text/60">
                    {item.slug}
                    {item.specializations?.length ? (
                      <span className="ms-2 rounded-full bg-primary/10 px-2 py-0.5 text-xs text-primary">
                        {item.specializations.length} {t('admin.recruitment.specializations')}
                      </span>
                    ) : null}
                  </div>
                </div>
                <div className="flex gap-1">
                  <button
                    type="button"
                    onClick={() => setExpandedId(expandedId === item.id ? null : item.id)}
                    className="p-2 text-primary"
                    title={t('admin.recruitment.manageSpecializations')}
                  >
                    {expandedId === item.id ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                  </button>
                  <button type="button" onClick={() => setForm(item)} className="p-2 text-primary">
                    <Pencil className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={async () => {
                      await recruitmentAdminApi.departments.delete(item.id)
                      load()
                    }}
                    className="p-2 text-red-600"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>

              {expandedId === item.id ? (
                <SpecializationsPanel department={item} onChanged={load} />
              ) : null}
            </Card>
          ))}
        </div>
      )}
    </div>
  )
}
